import { Button } from "@/components/atoms/Button";
import { StatusChip } from "@/components/atoms/StatusChip";
import { Typography } from "@/components/atoms/Typography";
import {
  ChevronRightIcon,
  CopyIcon,
  DirhamIcon,
  FileDownIcon,
  ReceiptTextWithBadgeCheckIcon,
  ReceiptTextWithBadgeClockIcon,
} from "@/components/atoms/icons";
import { OkDialog } from "@/components/molecules/dialogs/OkDialog";
import { BankTransferDetailsDialog } from "@/modules/properties-web/components/BankTransferDetailsDialog";
import {
  mapInstallmentBankDetailsToAccount,
  type BankTransferAccount,
} from "@/modules/properties-web/constants/bankTransferDetails";
import { TextHeader } from "@/components/molecules/header/TextHeader";
import { useWebDocumentTitle } from "@/core/hooks/useWebDocumentTitle";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { asyncStorage } from "@/core/storage";
import { useAppStore } from "@/core/stores/appStore";
import { downloadAndPersistDocument } from "@/modules/property-details/utils/documentDownload";
import * as Clipboard from "expo-clipboard";
import { router, useLocalSearchParams } from "expo-router";
import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  findInstallmentById,
  formatDueDate,
  formatDueDateWithPrefix,
  mapInstallmentStatusToPaymentStatus,
} from "./utils/installmentUtils";

/** Payment fields and Contentful values are not always strings at runtime. */
function displayText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

export interface PaymentDetailsScreenProps {
  paymentId?: string;
  onBackPress?: () => void;
  onSupportPress?: () => void;
  onPayNowPress?: () => void;
  /**
   * Optional side-effect that runs whenever Pay Now is pressed,
   * regardless of whether onPayNowPress is provided.
   * Used by web dialog wrapper to close the dialog before navigation.
   */
  onPayNowSideEffect?: () => void;
  onProFormaPress?: () => void;
  /**
   * Use X/close icon instead of back arrow (for dialogs)
   */
  useCloseIcon?: boolean;
  /**
   * Web dialog: close the payment details dialog and show bank details
   * outside it, so the two dialogs are not stacked.
   * Native opens the bank-transfer screen instead.
   */
  onViewBankDetails?: (account: BankTransferAccount | null) => void;
}

export default function PaymentDetailsScreen({
  paymentId: propPaymentId,
  onBackPress,
  onSupportPress,
  onPayNowPress,
  onPayNowSideEffect,
  onProFormaPress,
  useCloseIcon = false,
  onViewBankDetails,
}: PaymentDetailsScreenProps) {
  const PAYMENT_TX_MAP_KEY = "payment.transactions";
  const colors = useUIColors();
  const insets = useSafeAreaInsets();
  const { isRTL } = useI18nContext();
  const { t } = useTranslation();
  const params = useLocalSearchParams<{ id?: string }>();
  const user = useAppStore((state) => state.auth.user);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [isDownloadingProforma, setIsDownloadingProforma] = useState(false);
  const [downloadDialogVisible, setDownloadDialogVisible] = useState(false);
  const [downloadDialogMessage, setDownloadDialogMessage] = useState("");
  const [bankDetailsVisible, setBankDetailsVisible] = useState(false);
  const [transactionMap, setTransactionMap] = useState<
    Record<
      string,
      { transactionId: string; orderRef?: string; updatedAt: number }
    >
  >({});

  // Get paymentId from params or prop
  const paymentId = params.id || propPaymentId;

  useCatalogDynatraceView(
    DT_SCREEN.PAYMENT_DETAILS,
    'payment_details_screen_viewed',
    Boolean(paymentId),
  );

  useEffect(() => {
    const loadMap = async () => {
      try {
        const raw = (await asyncStorage.getItem(PAYMENT_TX_MAP_KEY)) || "{}";
        const parsed = JSON.parse(raw) as Record<
          string,
          { transactionId: string; orderRef?: string; updatedAt: number }
        >;
        setTransactionMap(parsed);
      } catch (error) {
        logger.warn("PaymentDetailsScreen", "Failed to load transaction map", {
          error,
        });
      }
    };
    void loadMap();
  }, []);

  // Get data from store
  const properties = useAppStore((state) => state.properties);
  const unitInstallments = useAppStore((state) => state.unitInstallments);
  const isAdhaJourney = useAppStore((state) => state.isAdhaJourney);

  // Find payment data
  const paymentItem = useMemo(() => {
    if (!paymentId) return null;
    return findInstallmentById(unitInstallments, properties, paymentId);
  }, [paymentId, unitInstallments, properties]);

  // Transform payment data for display
  const paymentData = useMemo(() => {
    if (!paymentItem) return null;

    // Helper function to translate payment type
    const translatePaymentType = (type: unknown): string => {
      const raw = displayText(type).trim();
      if (!raw) return "";
      const typeLower = raw.toLowerCase();
      const pick = (translated: unknown): string => {
        const text = displayText(translated).trim();
        return text.length > 0 ? text : raw;
      };
      if (typeLower.includes("handover")) return pick(t("propertyDetails.handover"));
      if (typeLower.includes("installment"))
        return pick(t("propertyDetails.installment"));
      if (typeLower.includes("down payment"))
        return pick(t("propertyDetails.downPayment"));
      if (typeLower.includes("adm fee") || typeLower.includes("adm"))
        return pick(t("propertyDetails.admFee"));
      if (
        typeLower.includes("technology fee") ||
        typeLower.includes("convenience fee")
      )
        return pick(t("payments.convenienceFee"));
      return raw || displayText(paymentItem.payment_name).trim();
    };

    const status = mapInstallmentStatusToPaymentStatus(
      paymentItem.category,
      paymentItem.status,
    );

    // Determine final status: if paid_amount < due_amount, it's in-process
    const finalStatus =
      paymentItem.category === "paid" &&
      paymentItem.paid_amount &&
      paymentItem.due_amount
        ? paymentItem.paid_amount < paymentItem.due_amount
          ? "in-process"
          : "paid"
        : status;

    // Format dates (backend only provides due date; use \"Due date\" label from payments)
    const dueDateFormatted = formatDueDate(paymentItem.due_date);
    const dueDateWithPrefix = formatDueDateWithPrefix(
      paymentItem.due_date,
      t("payments.dueDate"),
    );

    // For in-process payments, calculate amounts
    const dueAmount = paymentItem.due_amount || 0;
    const paidAmount = paymentItem.paid_amount || 0;
    const remainingAmount = dueAmount - paidAmount;
    const isInProcessPayment = finalStatus === "in-process";

    const rawType = paymentItem.type || paymentItem.payment_name;
    return {
      id: paymentItem.id,
      title: translatePaymentType(rawType),
      propertyName: displayText(paymentItem.propertyName).trim(),
      propertyId: paymentItem.propertyId,
      amount:
        paymentItem.category === "paid"
          ? paymentItem.paid_amount || paymentItem.amount || 0
          : paymentItem.due_amount || 0,
      dueAmount: dueAmount,
      paidAmount: paidAmount,
      remainingAmount: remainingAmount,
      dueDate: dueDateWithPrefix,
      dueDateFormatted,
      status: finalStatus as
        | "paid"
        | "missed"
        | "due-soon"
        | "in-process"
        | "future",
      installmentNumber: paymentItem.installment_number,
      type: rawType,
      typeDisplay: translatePaymentType(rawType),
      paymentType: paymentItem.payment_type, // 'installment' | 'other_charge'
      hasCheque: paymentItem.has_cheque,
      transactionId: transactionMap[paymentItem.id]?.transactionId || null,
      percentage: paymentItem.percentage, // Percentage of total amount
      isInProcessPayment,
      // ADHA loan Pay Now is a different screen. This flag only gates installment checkout.
      onlinePaymentEnabled:
        isAdhaJourney || paymentItem.isOnlinePaymentEnabled === true,
      bankAccount: mapInstallmentBankDetailsToAccount(paymentItem.bank_details),
    };
  }, [paymentItem, t, transactionMap, isAdhaJourney]);

  const handlePayNowPress = () => {
    if (!paymentData?.onlinePaymentEnabled) {
      return;
    }

    // Allow external side-effects (e.g. close web dialog) before navigation
    if (onPayNowSideEffect) {
      onPayNowSideEffect();
    }

    if (onPayNowPress) {
      onPayNowPress();
    } else if (paymentData) {
      // Navigate to property payments screen with this payment selected
      router.push(
        `/(auth)/property-payments?tab=upcoming&selectedPaymentIds=${paymentData.id}` as any,
      );
    }
  };

  const handleDownloadProForma = async () => {
    if (!paymentData) return;
    const customerId = user?.customer_id || user?.id;
    if (!customerId) {
      setDownloadDialogMessage(t("errors.unknownError"));
      setDownloadDialogVisible(true);
      return;
    }

    try {
      setIsDownloadingProforma(true);
      const result = await downloadAndPersistDocument({
        docType: "SALES_PORFORMA_INVOICE",
        parentId: paymentData.id,
        customerId,
        translationKey: "payments.downloadInvoice",
        t,
      });

      if (!result.success) {
        setDownloadDialogMessage(result.error || t("errors.unknownError"));
      } else {
        setDownloadDialogMessage(
          result.message || t("payments.downloadInvoice"),
        );
      }
      setDownloadDialogVisible(true);
    } catch (error: any) {
      logger.error(
        "PaymentDetailsScreen",
        "Download pro forma invoice failed",
        { error: error?.message || error },
      );
      setDownloadDialogMessage(t("errors.unknownError"));
      setDownloadDialogVisible(true);
    } finally {
      setIsDownloadingProforma(false);
    }
  };

  const handleCopyTransactionId = async () => {
    if (paymentData?.transactionId) {
      try {
        await Clipboard.setStringAsync(paymentData.transactionId);
        logger.debug(
          "PaymentDetailsScreen",
          "Transaction ID copied",
          paymentData.transactionId,
        );
        // TODO: Show toast notification
      } catch (error) {
        logger.error(
          "PaymentDetailsScreen",
          "Failed to copy transaction ID",
          error,
        );
      }
    }
  };

  const handleDownloadInvoice = async () => {
    if (!paymentData) return;
    const customerId = user?.customer_id || user?.id;
    if (!customerId) {
      setDownloadDialogMessage(t("errors.unknownError"));
      setDownloadDialogVisible(true);
      return;
    }

    try {
      setIsDownloadingInvoice(true);
      const result = await downloadAndPersistDocument({
        docType: "SALES_INVOICE",
        parentId: paymentData.id,
        customerId,
        translationKey: "payments.downloadInvoice",
        t,
      });

      if (!result.success) {
        setDownloadDialogMessage(result.error || t("errors.unknownError"));
      } else {
        setDownloadDialogMessage(
          result.message || t("payments.downloadInvoice"),
        );
      }
      setDownloadDialogVisible(true);
    } catch (error: any) {
      logger.error("PaymentDetailsScreen", "Download invoice failed", {
        error: error?.message || error,
      });
      setDownloadDialogMessage(t("errors.unknownError"));
      setDownloadDialogVisible(true);
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  const paymentDocumentTitle = paymentData
    ? [paymentData.title, paymentData.propertyName]
        .map((part) => displayText(part).trim())
        .filter((part) => part.length > 0)
        .join(" — ")
    : displayText(t("payments.paymentDetails"));
  useWebDocumentTitle(
    Platform.OS === "web" ? paymentDocumentTitle : undefined,
  );

  if (!paymentData) {
    return (
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}
      >
        <TextHeader
          title={t("payments.paymentDetails")}
          onBackPress={onBackPress}
          showSupportButton={true}
          useCloseIcon={useCloseIcon}
        />
        <View style={styles.errorContainer}>
          <Typography variant="body" color="secondary">
            {t("payments.paymentNotFound")}
          </Typography>
        </View>
      </View>
    );
  }

  const isDueSoon = paymentData.status === "due-soon";
  const isMissed = paymentData.status === "missed";
  const isInProcess = paymentData.status === "in-process";
  const isPaid = paymentData.status === "paid";
  const isFuture = paymentData.status === "future";
  const canPayThisInstallment = isDueSoon || isMissed || isFuture;
  const showOnlinePayAction =
    canPayThisInstallment && paymentData.onlinePaymentEnabled === true;
  // Same slot as Pay Now. Hidden whenever Pay Now is shown, including ADHA.
  const showViewBankDetails = !showOnlinePayAction && !isAdhaJourney && canPayThisInstallment;

  const handleViewBankDetailsPress = () => {
    if (!showViewBankDetails) {
      return;
    }
    if (onViewBankDetails) {
      onViewBankDetails(paymentData.bankAccount);
      return;
    }
    if (Platform.OS !== "web") {
      router.push({
        pathname: "/(auth)/bank-transfer-details",
        params: { viewOnly: "1", paymentId: paymentData.id },
      } as any);
      return;
    }
    setBankDetailsVisible(true);
  };

  // Determine which icon to use based on payment status
  const renderPaymentIcon = () => {
    if (isPaid) {
      return <ReceiptTextWithBadgeCheckIcon size={80} />;
    } else if (isInProcess) {
      // In-process uses grey clock badge
      return (
        <ReceiptTextWithBadgeClockIcon
          size={80}
          clockBadgeColor={UI_PRIMITIVES.white60}
        />
      );
    } else if (isMissed) {
      // Missed payments use redLightest2 for clock badge
      return (
        <ReceiptTextWithBadgeClockIcon
          size={80}
          clockBadgeColor={colors.fontErrorText}
        />
      );
    } else if (isDueSoon) {
      // Due-soon uses yellow clock badge
      return (
        <ReceiptTextWithBadgeClockIcon
          size={80}
          clockBadgeColor={UI_PRIMITIVES.yellowDark}
        />
      );
    } else if (isFuture) {
      // Future payments use blue clock badge
      return (
        <ReceiptTextWithBadgeClockIcon
          size={80}
          clockBadgeColor={UI_PRIMITIVES.bluePrimary}
        />
      );
    }
    // Fallback (should not happen)
    return (
      <ReceiptTextWithBadgeClockIcon
        size={80}
        clockBadgeColor={UI_PRIMITIVES.yellowDark}
      />
    );
  };

  let bottomAction: React.ReactNode = null;
  if (showOnlinePayAction) {
    bottomAction = (
      <View
        style={[styles.bottomSection, { paddingBottom: insets.bottom + 12 }]}
      >
        {/* NOTE: font size in feeNotice css is 12px as suggested by marketing team */}
        <Typography
          variant="footnote"
          color="tertiary"
          style={styles.feeNotice}
        >
          {t("payments.onlinePaymentFeeNotice")}
        </Typography>
        <Button
          title={t("payments.payNow")}
          variant="primary"
          onPress={handlePayNowPress}
          style={styles.payNowButton}
        />
      </View>
    );
  } else if (showViewBankDetails) {
    bottomAction = (
      <View
        style={[styles.bottomSection, { paddingBottom: insets.bottom + 12 }]}
      >
        <Button
          title={t("payments.viewBankDetails")}
          variant="primary"
          onPress={handleViewBankDetailsPress}
          style={styles.payNowButton}
        />
      </View>
    );
  }

  return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}
    >
      <TextHeader
        title={t("payments.paymentDetails")}
        onBackPress={onBackPress}
        showSupportButton={true}
        useCloseIcon={useCloseIcon}
      />

      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          !(showOnlinePayAction || showViewBankDetails) && styles.scrollContentCompact,
        ]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.content}>
          {/* Payment Summary Section */}
          <View style={styles.paymentSummary}>
            {/* Illustration - Different icon based on payment status */}
            <View style={styles.illustrationContainer}>
              {renderPaymentIcon()}
            </View>

            <Typography
              variant="title1"
              color="secondary"
              style={styles.paymentTitle}
            >
              {paymentData.title}
            </Typography>

            <Typography
              variant="body"
              color="secondary"
              style={styles.propertyName}
            >
              {paymentData.propertyName}
            </Typography>

            {/* Amount - Show different amounts based on status */}
            {isInProcess ? (
              // For in-process: Show due amount (total amount)
              <View
                style={[
                  styles.amountContainer,
                  { flexDirection: "row", direction: "ltr" },
                ]}
              >
                <DirhamIcon size={27} color={colors.fontPrimary} />
                <Typography
                  variant="largeTitle"
                  color="primary"
                  style={styles.amountText}
                >
                  {paymentData.dueAmount.toLocaleString()}
                </Typography>
              </View>
            ) : (
              // For other statuses: Show regular amount
              <View
                style={[
                  styles.amountContainer,
                  { flexDirection: "row", direction: "ltr" },
                ]}
              >
                <DirhamIcon size={27} color={colors.fontPrimary} />
                <Typography
                  variant="largeTitle"
                  color="primary"
                  style={styles.amountText}
                >
                  {paymentData.amount.toLocaleString()}
                </Typography>
              </View>
            )}

            {/* Date - always show \"Due by\" using backend due_date (no separate paid date) */}
            {(isDueSoon || isFuture || isInProcess || isPaid) && (
              <Typography
                variant="body"
                color="secondary"
                style={styles.dueDate}
              >
                {paymentData.dueDate}
              </Typography>
            )}

            {/* Transaction ID for paid items */}
            {isPaid && paymentData.transactionId && (
              <View style={styles.transactionIdContainer}>
                <Typography
                  variant="subhead"
                  color="primary"
                  style={styles.transactionIdText}
                >
                  {t("payments.transactionId")}: {paymentData.transactionId}
                </Typography>
                <TouchableOpacity
                  onPress={handleCopyTransactionId}
                  style={styles.copyButton}
                >
                  <CopyIcon size={20} color={colors.fontPrimary} />
                </TouchableOpacity>
              </View>
            )}

            <StatusChip
              status={
                paymentData.status as
                  | "paid"
                  | "missed"
                  | "due-soon"
                  | "in-process"
                  | "future"
              }
              size="small"
              textFirst={isRTL}
            />
          </View>

          {/* Download Invoice Button - Only for paid installment payments */}
          {isPaid && paymentData.paymentType === "installment" && (
            <View
              style={[styles.downloadInvoiceContainer, styles.loadingWrapper]}
            >
              <Button
                title={t("payments.downloadInvoice")}
                variant="text"
                onPress={handleDownloadInvoice}
                leading={
                  <FileDownIcon size={18} color={colors.buttonDefault} />
                }
                style={styles.downloadInvoiceButton}
                disabled={isDownloadingInvoice}
              />
              {isDownloadingInvoice && (
                <View
                  style={[
                    styles.loadingOverlay,
                    { backgroundColor: colors.backgroundLevel1 },
                  ]}
                >
                  <ActivityIndicator size="small" color={colors.fontPrimary} />
                </View>
              )}
            </View>
          )}

          {/* Notice Section - Different for each state */}
          {(isDueSoon || isMissed || isFuture) && (
            <View style={styles.noticeContainer}>
              <Typography
                variant="subhead"
                color="tertiary"
                style={styles.noticeText}
              >
                {t("payments.autoDeductionNotice")}
              </Typography>
            </View>
          )}

          {isInProcess && (
            <View style={styles.noticeContainer}>
              <Typography
                variant="subhead"
                color="tertiary"
                style={styles.noticeText}
              >
                {t("payments.receiptGenerationNotice")}
              </Typography>
            </View>
          )}

          {/* Payment Details Card */}
          <View style={styles.detailsCard}>
            {/* Pro Forma Invoice Section - Only for due-soon and future (not for missed or in-process) */}
            {(isDueSoon || isFuture) && (
              <>
                <View
                  style={[
                    styles.proFormaSection,
                    styles.loadingWrapper,
                    isRTL && { flexDirection: "row-reverse" },
                  ]}
                >
                  <View style={styles.proFormaLeft}>
                    {/* NOTE: font size variant="subhead" suggested by marketing team, to match the font size of the text */}
                    <Typography
                      variant="subhead"
                      color="primary"
                      style={[
                        styles.proFormaText,
                        { textAlign: isRTL ? "right" : "left" },
                      ]}
                    >
                      {t("payments.proFormaInvoiceQuestion")}
                    </Typography>
                  </View>
                  <Button
                    title={t("payments.downloadInvoice")}
                    variant="text"
                    onPress={handleDownloadProForma}
                    style={styles.proFormaButton}
                    trailing={
                      !isRTL ? (
                        <ChevronRightIcon
                          size={16}
                          color={colors.fontPrimary}
                        />
                      ) : undefined
                    }
                    leading={
                      isRTL ? (
                        <View style={{ transform: [{ scaleX: -1 }] }}>
                          <ChevronRightIcon
                            size={16}
                            color={colors.fontPrimary}
                          />
                        </View>
                      ) : undefined
                    }
                    disabled={isDownloadingProforma}
                  />
                  {isDownloadingProforma && (
                    <View
                      style={[
                        styles.loadingOverlay,
                        { backgroundColor: colors.backgroundLevel1 },
                      ]}
                    >
                      <ActivityIndicator
                        size="small"
                        color={colors.fontPrimary}
                      />
                    </View>
                  )}
                </View>

                {/* Divider */}
                <View style={styles.divider} />
              </>
            )}

            {/* Payment Details */}
            {/* Show installment number only for installment type payments */}
            {paymentData.paymentType === "installment" &&
              paymentData.installmentNumber && (
                <Typography
                  variant="subhead"
                  color="primary"
                  style={[
                    styles.installmentNumber,
                    { textAlign: isRTL ? "right" : "left" },
                  ]}
                >
                  {t("payments.installmentNumber").replace(
                    "#{number}",
                    `#${paymentData.installmentNumber}`,
                  )}
                </Typography>
              )}

            {/* % of total amount - only for paid */}
            {isPaid && paymentData.percentage !== undefined && (
              <View
                style={[
                  styles.detailRow,
                  isRTL && {
                    justifyContent: "space-between",
                    gap: 0,
                  },
                ]}
              >
                {isRTL ? (
                  <>
                    {/* RTL: Value first (left), Label second (right) */}
                    <Typography
                      variant="body"
                      color="primary"
                      style={[
                        styles.detailValue,
                        {
                          textAlign: "left",
                          flex: 0,
                          flexShrink: 0,
                          flexGrow: 0,
                        },
                      ]}
                    >
                      {paymentData.percentage}%
                    </Typography>
                    {(() => {
                      const labelText = displayText(t("payments.percentOfTotalAmount"));
                      // Extract % sign from the beginning of the text for RTL positioning
                      const hasPercentAtStart = labelText
                        .trim()
                        .startsWith("%");
                      const percentSign = hasPercentAtStart ? "%" : "";
                      const textWithoutPercent = hasPercentAtStart
                        ? labelText.trim().substring(1).trim()
                        : labelText;

                      return (
                        <View
                          style={{
                            flex: 1,
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            minWidth: 0,
                          }}
                        >
                          {percentSign && (
                            <Typography
                              variant="subhead"
                              color="tertiary"
                              style={{ textAlign: "left", marginLeft: 4 }}
                            >
                              {percentSign}
                            </Typography>
                          )}
                          <Typography
                            variant="subhead"
                            color="tertiary"
                            numberOfLines={1}
                            ellipsizeMode="tail"
                            style={[
                              styles.detailLabel,
                              {
                                textAlign: "right",
                                flexShrink: 1,
                              },
                            ]}
                          >
                            {textWithoutPercent}
                          </Typography>
                        </View>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    {/* LTR: Label first (left), Value second (right) */}
                    <Typography
                      variant="subhead"
                      color="tertiary"
                      style={styles.detailLabel}
                    >
                      {t("payments.percentOfTotalAmount")}
                    </Typography>
                    <Typography
                      variant="body"
                      color="primary"
                      style={[styles.detailValue, { textAlign: "right" }]}
                    >
                      {paymentData.percentage}%
                    </Typography>
                  </>
                )}
              </View>
            )}

            {/* Amount breakdown for in-process payments */}
            {isInProcess && (
              <>
                <View style={styles.detailRow}>
                  <Typography
                    variant="subhead"
                    color="tertiary"
                    style={styles.detailLabel}
                  >
                    {t("payments.paidAmount")}
                  </Typography>
                  <View
                    style={[
                      styles.detailValue,
                      { alignItems: isRTL ? "flex-start" : "flex-end" },
                    ]}
                  >
                    <View
                      style={[
                        styles.amountRow,
                        { flexDirection: "row", direction: "ltr" },
                      ]}
                    >
                      <DirhamIcon size={16} color={colors.fontPrimary} />
                      <Typography variant="body" color="primary" style={styles.detailAmountText}>
                        {paymentData.paidAmount.toLocaleString()}
                      </Typography>
                    </View>
                  </View>
                </View>
                <View style={styles.detailRow}>
                  <Typography
                    variant="subhead"
                    color="tertiary"
                    style={styles.detailLabel}
                  >
                    {t("payments.remainingAmount")}
                  </Typography>
                  <View
                    style={[
                      styles.detailValue,
                      { alignItems: isRTL ? "flex-start" : "flex-end" },
                    ]}
                  >
                    <View
                      style={[
                        styles.amountRow,
                        { flexDirection: "row", direction: "ltr" },
                      ]}
                    >
                      <DirhamIcon size={16} color={colors.fontPrimary} />
                      <Typography variant="body" color="primary" style={styles.detailAmountText}>
                        {paymentData.remainingAmount.toLocaleString()}
                      </Typography>
                    </View>
                  </View>
                </View>
              </>
            )}

            <View
              style={[
                styles.detailRow,
                isRTL && { flexDirection: "row-reverse" },
              ]}
            >
              <Typography
                variant="subhead"
                color="tertiary"
                style={styles.detailLabel}
              >
                {t("payments.type")}
              </Typography>
              <Typography
                variant="body"
                color="primary"
                style={[
                  styles.detailValue,
                  { textAlign: isRTL ? "left" : "right" },
                ]}
              >
                {paymentData.typeDisplay}
              </Typography>
            </View>

            <View
              style={[
                styles.detailRow,
                isRTL && { flexDirection: "row-reverse" },
              ]}
            >
              <Typography
                variant="subhead"
                color="tertiary"
                style={styles.detailLabel}
              >
                {t("payments.dueDate")}
              </Typography>
              <Typography
                variant="body"
                color="primary"
                style={[
                  styles.detailValue,
                  { textAlign: isRTL ? "left" : "right" },
                ]}
              >
                {paymentData.dueDateFormatted}
              </Typography>
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Pay Now and View Bank Details share one slot. Only one can render. */}
      {bottomAction}

      <OkDialog
        visible={downloadDialogVisible}
        message={downloadDialogMessage}
        onOk={() => setDownloadDialogVisible(false)}
      />
      {Platform.OS === "web" && showViewBankDetails && !onViewBankDetails ? (
        <BankTransferDetailsDialog
          visible={bankDetailsVisible}
          viewOnly
          account={paymentData.bankAccount}
          onBack={() => setBankDetailsVisible(false)}
          onClose={() => setBankDetailsVisible(false)}
          onConfirmTransfer={() => setBankDetailsVisible(false)}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 100, // Space for the absolute Pay Now section
  },
  scrollContentCompact: {
    flexGrow: 0,
    paddingBottom: 0,
  },
  content: {
    paddingHorizontal: 16,
    paddingTop: 24,
    gap: 24,
  },
  paymentSummary: {
    alignItems: "center",
    gap: 8,
  },
  illustrationContainer: {
    width: 80,
    height: 80,
    justifyContent: "center",
    alignItems: "center",
  },
  paymentTitle: {
    textAlign: "center",
  },
  propertyName: {
    textAlign: "center",
  },
  amountContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  amountText: {
    textAlign: "center",
    paddingBottom: 4,
  },
  detailAmountText: {
    paddingBottom: 4,
  },
  dueDate: {
    textAlign: "center",
  },
  transactionIdContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  transactionIdText: {
    textAlign: "center",
  },
  copyButton: {
    padding: 4,
  },
  downloadInvoiceContainer: {
    alignItems: "center",
    paddingVertical: 12,
    position: "relative",
  },
  downloadInvoiceButton: {
    alignSelf: "center",
  },
  noticeContainer: {
    backgroundColor: "#f5f5f5",
    padding: 12,
    borderRadius: 8,
    alignItems: "center",
  },
  noticeText: {
    textAlign: "center",
  },
  detailsCard: {
    borderWidth: 1,
    borderColor: "#e3e3e3",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 16,
    gap: 12,
  },
  proFormaSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    position: "relative",
  },
  loadingWrapper: {
    position: "relative",
  },
  proFormaLeft: {
    flex: 1,
  },
  proFormaText: {
    lineHeight: 20,
  },
  proFormaButton: {
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 8,
    opacity: 0.8,
  },
  divider: {
    height: 1,
    backgroundColor: "#e3e3e3",
    width: "100%",
  },
  installmentNumber: {
    textAlign: "left",
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  detailLabel: {
    flexShrink: 0,
  },
  detailValue: {
    flex: 1,
  },
  bottomSection: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    paddingTop: 12,
    gap: 12,
    backgroundColor: "transparent",
  },
  feeNotice: {
    textAlign: "center",
    fontSize: 12,
  },
  payNowButton: {
    width: "100%",
  },
  downloadButton: {
    alignSelf: "center",
  },
  amountRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
});
